import { InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Drawer, message, Radio, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import SkillSetControl from "./SkillSetControl";
import useAxios from "../../../hooks/useAxios";
import { getAllLocations } from "../../../services/locations/locations.service";

const OPTIONS = [
  {
    label: "Active",
    value: true,
  },
  {
    label: "Inactive",
    value: false,
  },
];

const MappingConfigDrawer = ({
  openConfigModal,
  toggleMappingModal,
  designations,
  departments,
  mapping,
  processes,
  setLoad,
  fetchConfigurations,
  updateConfigurationRows,
  isEdited,
  setIsEdited,
}) => {
  const api = useAxios();
  const [departmentId, setDepartmentId] = useState([]);
  const [designationId, setDesignationId] = useState([]);
  const [processId, setProcessId] = useState([]);
  const [versantType, setVersantType] = useState("voice");
  const [writtenScore, setWrittenScore] = useState(null);
  const [voiceScore, setVoiceScore] = useState(null);
  const [status, setStatus] = useState(true);
  const [error, setError] = useState(null);
  const [locationId, setLocationId] = useState([]);
  const [locationsData, setLocationsData] = useState([]);

  useEffect(() => {
    if (!isEdited) return;
    setDesignationId([isEdited?.designation?._id] || []);
    setDepartmentId([isEdited?.department?._id] || []);
    setProcessId([isEdited?.process?._id] || []);
    const locId = isEdited?.location?._id || (typeof isEdited?.location === 'string' ? isEdited?.location : null) || isEdited?.locationId;
    setLocationId(locId ? [locId] : []);
    setVersantType(isEdited?.versantType || "voice");
    setStatus(isEdited?.isActive);
    setVoiceScore(isEdited?.voiceScore || null);
    setWrittenScore(isEdited?.writtenScore || null);
  }, [isEdited]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { data } = await getAllLocations(api, "user");
        const list = data?.locationMaster || [];
        const allowedLower = ["amritsar", "gurugram", "vadodara", "kolkata", "bangalore", "bengaluru"];
        const filtered = list.filter(
          (loc) =>
            loc?.locationName &&
            allowedLower.includes(loc.locationName.toLowerCase())
        );
        setLocationsData(filtered);
      } catch (err) {
        console.error("Error fetching locations:", err);
      }
    };
    fetchLocations();
  }, []);

  const filteredDesignations = useMemo(() => {
    if (!departmentId?.length) return designations;

    const _filter = designations.filter((d) =>
      mapping.some(
        (m) =>
          departmentId.includes(m.departmentId?._id) &&
          m.designationId?._id === d._id,
      ),
    );

    return _filter;
  }, [designations, departmentId, mapping]);

  const filteredDepartments = useMemo(() => {
    if (!designationId?.length) return departments;
    const _filter = departments.filter((dept) =>
      mapping.some(
        (m) =>
          designationId.includes(m.designationId?._id) &&
          m.departmentId?._id === dept._id,
      ),
    );

    return _filter;
  }, [designationId, departments, mapping]);

  const checkValidation = () => {
    let e = {};
    if (departmentId.length === 0) {
      e.department = "Department is required field.";
    }

    if (designationId.length === 0) {
      e.designation = "Role is required field.";
    }

    if (processId.length === 0) {
      e.process = "Process is required field.";
    }

    if (locationId.length === 0) {
      e.location = "Location is required field.";
    }

    if (versantType?.toLowerCase() === "both") {
      if (!voiceScore) {
        e.voiceScore = "Voice score is required field.";
      }
      if (!writtenScore) {
        e.writtenScore = "Written score is required field.";
      }
    }

    if (versantType?.toLowerCase() === "voice") {
      if (!voiceScore) {
        e.voiceScore = "Voice score is required field.";
      }
    }

    if (versantType?.toLowerCase() === "written") {
      if (!writtenScore) {
        e.writtenScore = "Written score is required field.";
      }
    }

    if (Object.keys(e).length !== 0) {
      setError(e);
      return true;
    }

    return false;
  };

  const buildConfigurationPayload = () => {
    const selectedLocations = Array.isArray(locationId)
      ? locationId
      : locationId
        ? [locationId]
        : [];

    const configuration = {
      versantType,
      voiceScore:
        versantType === "voice" || versantType === "both" ? voiceScore : null,
      writtenScore:
        versantType === "written" || versantType === "both"
          ? writtenScore
          : null,
      isActive: status,
    };

    const result = mapping
      .filter(
        (m) =>
          departmentId.includes(m.departmentId?._id) &&
          designationId.includes(m.designationId?._id),
      )
      .map((m) => ({
        ...configuration,
        departmentId: m.departmentId._id,
        designationId: m.designationId._id,
      }));

    return processId.flatMap((p) =>
      selectedLocations.flatMap((location) =>
        result.map((item) => ({
          ...item,
          processId: p,
          location,
        })),
      ),
    );
  };

  const saveConfiguration = async () => {
    try {
      setError(null);

      if (checkValidation()) return;

      const arr = buildConfigurationPayload();

      setLoad(true);

      await api.post(
        `/processBasedVersant`,
        {
          data: arr,
        },
        { tokenType: "user" },
      );

      await fetchConfigurations();
      handleClose();
      message.success(`Configuration save successfully.`);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoad(false);
    }
  };

  const updateConfiguration = async () => {
    try {
      setError(null);

      if (checkValidation()) return;

      const arr = buildConfigurationPayload();

      setLoad(true);

      const response = await api.post(
        `/processBasedVersant`,
        {
          data: arr,
        },
        { tokenType: "user" },
      );

      updateConfigurationRows(
        arr,
        response?.data?.data,
      );
      handleClose();
      message.success(`Configuration update successfully.`);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoad(false);
    }
  };

  const handleClose = () => {
    setIsEdited(null);
    toggleMappingModal();
  };

  const onChangeDepartment = (value) => {
    if (value) {
      setDepartmentId(value);
      setDesignationId((prev) => {
        return prev.filter((ele) =>
          mapping.some(
            (m) =>
              value.includes(m.departmentId?._id) &&
              m.designationId?._id === ele,
          ),
        );
      });
    } else {
      setDepartmentId([]);
      setDesignationId([]);
    }
  };

  const onChangeDesignation = (value) => {
    if (value) {
      setDesignationId(value);
      const _filter = departments.filter((dept) =>
        mapping.some(
          (m) =>
            value?.includes(m.designationId?._id) &&
            m.departmentId?._id === dept._id,
        ),
      );
      setDepartmentId(_filter?.map((ele) => ele?._id));
    } else {
      setDepartmentId([]);
      setDesignationId([]);
    }
  };

  return (
    <Drawer
      title="Create New Mapping"
      width={550}
      zIndex={9}
      placement="right"
      closeIcon={false}
      onClose={handleClose}
      open={openConfigModal}
      extra={<Button onClick={handleClose}>X</Button>}
    >
      <div className="d-flex flex-column gap-3" style={{ height: "100%" }}>
        <div className="row" style={{ flex: 1 }}>
          <div className="col-12">
            <div className="row">
              <div className="col-12">
                <div className="w-100 d-flex flex-column gap-1">
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <label>
                        ROLE <span className="text-red-600">*</span>
                      </label>
                      <div>
                        <Select
                          mode="multiple"
                          allowClear
                          className="filter-group-select w-100 p-0 outline-none bg-white cutom_select_advance"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return (
                              String(option?.children ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                              String(option?.value ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            );
                          }}
                          placeholder="e.g. Executive"
                          style={{ width: 300 }}
                          value={designationId}
                          onChange={(value) => {
                            onChangeDesignation(value ?? null);
                          }}
                        >
                          {filteredDesignations?.map((designation) => {
                            return (
                              <Select.Option value={designation?._id}>
                                {designation?.designation}
                              </Select.Option>
                            );
                          })}
                        </Select>
                        {error?.designation && (
                          <p className="text-red-500 mb-0">
                            {error?.designation}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 mb-4">
                      <label>
                        DEPARTMENT <span className="text-red-600">*</span>
                      </label>
                      <div>
                        <Select
                          mode="multiple"
                          allowClear
                          className="filter-group-select w-100 p-0 outline-none bg-white cutom_select_advance"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return (
                              String(option?.children ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                              String(option?.value ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            );
                          }}
                          placeholder="e.g. Operation"
                          style={{ width: 300 }}
                          value={departmentId}
                          onChange={(value) => {
                            onChangeDepartment(value ?? null);
                          }}
                        >
                          {filteredDepartments?.map((department) => {
                            return (
                              <Select.Option value={department?._id}>
                                {department?.department ||
                                  department?.departmentName}
                              </Select.Option>
                            );
                          })}
                        </Select>
                        {error?.department && (
                          <p className="text-red-500 mb-0">
                            {error?.department}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <label>
                        PROCESS (CLIENT NAME){" "}
                        <span className="text-red-600">*</span>
                        {/* <i className="text-[12px] text-[#bfbfbf] font-normal">
                          (Optional)
                        </i> */}
                      </label>
                      <div>
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          className="filter-group-select w-100 p-0 outline-none bg-white cutom_select_advance"
                          placeholder="Select Process Name"
                          value={processId}
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return (
                              option?.children
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0 ||
                              option.value
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            );
                          }}
                          onChange={(value) => {
                            setProcessId(value ?? null);
                          }}
                        >
                          {processes?.map((source, index) => {
                            const { name, status, _id } = source;
                            if (!status) return null;
                            return (
                              <Select.Option key={index} value={_id}>
                                {name}
                              </Select.Option>
                            );
                          })}
                        </Select>
                        {error?.process && (
                          <p className="text-red-500 mb-0">{error?.process}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-12 mt-4">
                      <label>
                        LOCATION <span className="text-red-600">*</span>
                      </label>
                      <div>
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          className="filter-group-select w-100 p-0 outline-none bg-white cutom_select_advance"
                          placeholder="Select Location"
                          value={locationId}
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return (
                              option?.children
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0 ||
                              option.value
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            );
                          }}
                          onChange={(value) => {
                            setLocationId(value ?? null);
                          }}
                        >
                          {locationsData?.map((loc, index) => {
                            const { locationName } = loc;
                            return (
                              <Select.Option key={index} value={locationName}>
                                {locationName}
                              </Select.Option>
                            );
                          })}
                        </Select>
                        {error?.location && (
                          <p className="text-red-500 mb-0">{error?.location}</p>
                        )}
                      </div>
                    </div>
                    {/* <div className="col-12 mb-4 mt-0">
                      <span className="text-[12px] text-[#bfbfbf] font-normal">
                        Selecting a process makes this a specific mapping.
                        Leaving it blank applies it generically to the Role +
                        Department
                      </span>
                    </div> */}
                    <div className="col-12 my-4 mt-0">
                      <label>VERSANT TYPE</label>
                      <div
                        className="d-flex flex-column"
                        style={{ gap: "0.5rem" }}
                      >
                        <Radio.Group
                          buttonStyle="solid"
                          style={{
                            width: "100%",
                            marginBottom: 5,
                            display: "flex",
                            gap: "5px",
                          }}
                          value={versantType}
                          onChange={(e) => {
                            // if (voiceScore) setVoiceScore(null);
                            // if (writtenScore) setWrittenScore(null);
                            setVersantType(e.target.value);
                          }}
                        >
                          <Radio.Button
                            className="flex-fill text-center"
                            value={"voice"}
                          >
                            Voice
                          </Radio.Button>
                          <Radio.Button
                            className="flex-fill text-center"
                            value={"written"}
                          >
                            Written
                          </Radio.Button>
                          <Radio.Button
                            className="flex-fill text-center"
                            value={"both"}
                          >
                            Both
                          </Radio.Button>
                        </Radio.Group>
                      </div>
                    </div>
                    <div className="col-12 mb-4 mt-0">
                      <div
                        className="p-4 bg-[#ddedfe63] flex flex-col gap-1"
                        style={{
                          borderRadius: "15px",
                          border: "1px solid #254568",
                        }}
                      >
                        <div className="flex gap-[15px] items-center">
                          <InfoCircleOutlined className="!text-[16px] !text-[#254568]" />
                          <span className="font-semibold text-[#254568] text-[16px]">
                            SKILL SET CRITERIA
                          </span>
                        </div>
                        <div>
                          <SkillSetControl
                            error={error}
                            voiceScore={voiceScore}
                            setVoiceScore={setVoiceScore}
                            writtenScore={writtenScore}
                            setWrittenScore={setWrittenScore}
                            label={versantType}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-4 mt-0">
                      <label>STATUS</label>
                      <div className="mt-1">
                        <Radio.Group
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          block
                          options={OPTIONS}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row pb-4">
          <div className="col-12">
            <div className="pt-3 d-flex gap-1">
              <button
                onClick={handleClose}
                className="save-filter-btn text-black rounded px-4 py-2 flex-1 text-[16px] font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (isEdited) {
                    updateConfiguration();
                    return;
                  }

                  saveConfiguration();
                }}
                className="btn btn-primary flex gap-[10px] items-center justify-center flex-1"
              >
                <SaveOutlined style={{ fontSize: "1.1rem", fontWeight: 600 }} />
                <span className="flex items-center text-[16px] font-semibold">
                  {isEdited ? `Update` : `Save`} Configuration
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default MappingConfigDrawer;
